import { IconAnalyze, IconCalendar, IconCalendarUser, IconEye, IconEyeOff, IconRepeat } from '@tabler/icons-react'
import uniq from 'lodash/uniq'
import { useState } from 'react'
import { NavigateFunction, useLocation, useNavigate } from 'react-router'

import { NavLink } from '~/app/navLinks'
import { PractitionerPreviewContent } from '~/app/pages/shared/Practitioner/PractitionerPreview'
import { basePaths, pages } from '~/app/routes'
import { AstraIcon, KanbanIcon, SidebarRightIcon } from '~/astra'
import { useFlag } from '~/hooks/useFlag'
import { useNavLinks } from '~/hooks/useNavLinks'
import { useStore } from '~/store/store'
import { PRACTITIONER_DIPS_RESOURCE_INCLUDE, PractitionerWithDipsResource, usePractitioners } from '~/storm'
import { getPractitionerFullname } from '~/utils/dips'

import { useSetDialog } from '../DialogRouter/useDialogRouter'
import { PractitionerChipWithTooltip } from '../PractitionerChip'
import { CommandPaletteOption } from './CommandPaletteOption'
import { CommandAction, CommandActionGroup } from './types'
import { ConfirmActionOptions, useConfirmAction } from './useConfirmAction'

const PRIVACY_MODE_UNSAVED_CHANGES_DESCRIPTION = 'Ulagrede endringer vil gå tapt.'

const getPrivacyModeConfirmOptions = (isPrivacyModeEnabled: boolean): ConfirmActionOptions =>
    isPrivacyModeEnabled
        ? {
              title: 'Vis persondata?',
              description: PRIVACY_MODE_UNSAVED_CHANGES_DESCRIPTION,
              cancelButtonLabel: 'Avbryt',
              confirmButtonLabel: 'Vis persondata',
          }
        : {
              title: 'Skjul persondata?',
              description: PRIVACY_MODE_UNSAVED_CHANGES_DESCRIPTION,
              cancelButtonLabel: 'Avbryt',
              confirmButtonLabel: 'Skjul persondata',
          }

type CommandWithExclude = CommandAction & {
    exclude: boolean
}

type AddToHistory = (command: CommandAction) => void

const createWithHistory =
    (addToHistory: AddToHistory) =>
    (command: CommandAction): CommandAction => ({
        ...command,
        onExecute: async (...args: Parameters<NonNullable<CommandAction['onExecute']>>) => {
            const result = await command.onExecute?.(...args)
            if (result !== false) {
                addToHistory(command)
            }
            return result
        },
    })

const createPractitionerCommand = (practitioner: PractitionerWithDipsResource, setDialog: ReturnType<typeof useSetDialog>): CommandAction => ({
    id: `practitioner-${practitioner.id}`,
    label: getPractitionerFullname(practitioner),
    keywords: [practitioner.short_name, ...(practitioner.dipsResource?.name ? [practitioner.dipsResource.name] : [])],
    icon: (
        <span className="min-w-[70px]">
            <PractitionerChipWithTooltip shortName={practitioner.short_name} fullName={getPractitionerFullname(practitioner)} />
        </span>
    ),
    onExecute: () => {
        setDialog({ activeDialog: 'practitioner', dialogData: { practitioner: practitioner } })
    },
    focusPreviewRender: () => <PractitionerPreviewContent practitioner={practitioner} />,
})

const createNavigationCommands = ({ navigate, currentPath, navLinks }: { navigate: NavigateFunction; currentPath: string; navLinks: NavLink[] }) =>
    navLinks.map(navLink => ({
        id: `navigation-${navLink.title}`,
        icon: (
            <span className="inline-flex h-[16px] w-[16px]">
                <navLink.icon />
            </span>
        ),
        label: `Naviger til ${navLink.title}`,
        keywords: [navLink.title, 'naviger'],
        onExecute: () => {
            void navigate(navLink.url)
        },
        exclude: currentPath === navLink.url,
    })) satisfies CommandWithExclude[]

const ToggleSidePanelCommandEntry = () => {
    const isOpen = useStore(state => state.surgeonScheduler.isDrawerOpen)
    const label = isOpen ? 'Lukk sidepanel' : 'Åpne sidepanel'
    return <CommandPaletteOption icon={<SidebarRightIcon />} label={label} />
}

const PrivacyModeCommandEntry = () => {
    const isPrivacyModeEnabled = useStore(state => state.app.isPrivacyMode)
    const label = isPrivacyModeEnabled ? 'Vis persondata' : 'Skjul persondata'
    return <CommandPaletteOption icon={<AstraIcon icon={isPrivacyModeEnabled ? IconEye : IconEyeOff} />} label={label} />
}

const createStaffingCommands = (context: CurrentContext) => {
    const surgeonSchedulerViews = pages['SURGEON_SCHEDULER'].views
    const isPractitionerView = context.currentPath.includes(surgeonSchedulerViews['practitioners'].path)
    const oppositeView = isPractitionerView ? surgeonSchedulerViews['locations'] : surgeonSchedulerViews['practitioners']
    return [
        {
            id: 'staffing-sidebar-toggle',
            label: 'Veksle sidepanel',
            keywords: ['sidebar', 'toggle', 'åpne', 'lukk'],
            renderOption: ToggleSidePanelCommandEntry,
            onExecute: () => {
                useStore.getState().surgeonScheduler.actions.toggleDrawer()
            },
            icon: <SidebarRightIcon />,
        },
        {
            id: 'staffing-sidebar-wishes',
            label: 'Åpne ønsker',
            keywords: ['sidepanel', 'wishes'],
            onExecute: () => {
                useStore.getState().surgeonScheduler.actions.openDrawer()
                useStore.getState().surgeonScheduler.actions.setActiveDrawerTab('wishes')
            },
            icon: <SidebarRightIcon />,
        },
        {
            id: 'staffing-sidebar-insights',
            label: 'Åpne innsikt',
            keywords: ['sidepanel', 'innsikt', 'insights'],
            onExecute: () => {
                useStore.getState().surgeonScheduler.actions.openDrawer()
                useStore.getState().surgeonScheduler.actions.setActiveDrawerTab('overview')
            },
            icon: <AstraIcon icon={IconAnalyze} />,
        },
        {
            id: 'staffing-view-toggle',
            label: `Endre visning: ${oppositeView.title}`,
            keywords: ['endre', 'visning'],
            onExecute: () => {
                const oppositeViewPath = oppositeView.path
                void context.navigate(oppositeViewPath)
            },
            icon: <AstraIcon icon={IconCalendarUser} size="small" />,
        },
    ]
}

const ToggleCalenderBoardView = () => {
    const selectedView = useStore(state => state.operationalPlannerViewOptions.selectedLayouts)?.week

    return (
        <CommandPaletteOption
            icon={selectedView === 'calendar' ? <KanbanIcon /> : <AstraIcon icon={IconCalendar} />}
            label={selectedView === 'calendar' ? 'Endre visning: tavle' : 'Endre visning: kalender'}
        />
    )
}

type CurrentContext = {
    currentPath: string
    navigate: NavigateFunction
}

const createOperationalPlannerCommands = ({ navigate, currentPath }: CurrentContext) => [
    {
        id: 'operational-view-week-calendar-toggle',
        label: 'Endre visning',
        keywords: ['operational', 'planner', 'kalender', 'tavle', 'board', 'visning'],
        onExecute: () => {
            const selectedView = useStore.getState().operationalPlannerViewOptions.selectedLayouts?.week
            useStore.getState().operationalPlannerViewOptions.actions.setSelectedLayout('week', selectedView === 'calendar' ? 'board' : 'calendar')
        },
        renderOption: ToggleCalenderBoardView,
        exclude: currentPath !== `${basePaths.OR_SCHEDULE}/calendar`,
    },
    {
        id: 'operational-planner-day-view',
        label: 'Endre visning: dag',
        keywords: ['operational', 'planner', 'kalender', 'tavle', 'board', 'visning'],
        onExecute: () => {
            void navigate(`${basePaths.OR_SCHEDULE}/day`)
        },
        icon: <AstraIcon icon={IconCalendar} />,
        exclude: currentPath === `${basePaths.OR_SCHEDULE}/day`,
    },
    {
        id: 'operational-planner-week-view',
        label: 'Endre visning: uke',
        keywords: ['operational', 'planner', 'kalender', 'tavle', 'board', 'visning'],
        onExecute: () => {
            void navigate(`${basePaths.OR_SCHEDULE}/calendar`)
        },
        icon: <AstraIcon icon={IconCalendar} />,
        exclude: currentPath === `${basePaths.OR_SCHEDULE}/calendar`,
    },

    {
        id: 'operational-planner-month-view',
        label: 'Endre visning: måned',
        keywords: ['måned', 'visning'],
        onExecute: () => {
            void navigate(`${basePaths.OR_SCHEDULE}/month`)
        },
        icon: <AstraIcon icon={IconCalendar} />,
        exclude: currentPath === `${basePaths.OR_SCHEDULE}/month`,
    },
]

type UseInitializeCommandsOptions = {
    confirm: ReturnType<typeof useConfirmAction>['confirm']
}

export const useInitializeCommands = ({ confirm }: UseInitializeCommandsOptions) => {
    const [lastUsed, setLastUsed] = useState<string[]>([])
    const includeRecurringAssignments = useFlag('surgeon_scheduler__recurring_assignments')
    const isPrivacyModeFeatureFlagEnabled = useFlag('privacy_mode')

    const setDialog = useSetDialog()

    const { pathname } = useLocation()
    const navigate = useNavigate()
    const practitioners = usePractitioners({ include: PRACTITIONER_DIPS_RESOURCE_INCLUDE })

    const addToHistory = (command: CommandAction) => {
        setLastUsed(prev => uniq([command.id, ...prev.slice(0, 1)]))
    }
    const withHistory = createWithHistory(addToHistory)

    const isInPage = (page: keyof typeof pages) => {
        const views = pages[page].views
        return Object.values(views).some(view => pathname.includes(view.path))
    }

    const { filteredFlatNavLinks: navLinks } = useNavLinks()
    const currentContext: CurrentContext = {
        currentPath: pathname,
        navigate,
    }
    const practitionerCommands = practitioners.map(p => createPractitionerCommand(p, setDialog))

    const contextualCommands = new Map<string, { exclude: boolean; command: CommandAction }>([
        ...createOperationalPlannerCommands(currentContext).map(c => [c.id, { command: c, exclude: c.exclude || !isInPage('OPERATIONAL_PLANNER') }] as const),
        ...createStaffingCommands(currentContext).map(c => [c.id, { command: c, exclude: !isInPage('SURGEON_SCHEDULER') }] as const),
        ...createNavigationCommands({ ...currentContext, navLinks }).map(c => [c.id, { command: c, exclude: c.exclude }] as const),
    ])

    const filteredContextualCommands = Array.from(contextualCommands.values())
        .filter(value => !value.exclude)
        .map(value => value.command)

    const commands: CommandActionGroup[] = [
        {
            type: 'group',
            label: 'Kirurger',
            options: practitionerCommands.map(withHistory),
        },
        {
            type: 'group',
            label: 'Kommandoer',
            options: [
                ...(isPrivacyModeFeatureFlagEnabled
                    ? [
                          {
                              id: 'privacy-mode-toggle',
                              label: 'Skjul persondata',
                              keywords: ['persondata', 'personvern', 'privat', 'privacy', 'skjul', 'vis', 'gdpr', 'skjermdeling'],
                              renderOption: PrivacyModeCommandEntry,
                              onExecute: async () => {
                                  const isPrivacyModeEnabled = useStore.getState().app.isPrivacyMode
                                  const confirmed = await confirm(getPrivacyModeConfirmOptions(isPrivacyModeEnabled))
                                  if (!confirmed) return false

                                  useStore.getState().app.actions.togglePrivacyMode()
                              },
                              icon: <AstraIcon icon={IconEyeOff} />,
                          },
                      ]
                    : []),
                ...(includeRecurringAssignments
                    ? [
                          {
                              id: 'recurring-assignments',
                              label: 'Repeterende regler',
                              onExecute: () => setDialog({ activeDialog: 'recurringAssignments', dialogData: undefined }),
                              icon: <AstraIcon icon={IconRepeat} />,
                          },
                      ]
                    : []),
                ...filteredContextualCommands,
            ]
                .map(o => withHistory(o))
                .sort((a, b) => a.label.localeCompare(b.label)),
        },
    ]
    commands.unshift({
        type: 'group',
        label: 'Sist besøkt',
        options: lastUsed
            .map(lastUsedId => {
                const command = commands
                    .flatMap(group => group.options)
                    .find(command => command.id === lastUsedId && (contextualCommands.get(lastUsedId)?.exclude ?? false) === false)
                if (!command) return null
                // need unique ids in the list
                return {
                    ...command,
                    id: `${command.id}-last-used`,
                }
            })
            .filter(Boolean),
    })

    return commands
}
